/*
 * Copyright 2026 Hypergiant Galactic Systems Inc. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */
'use client';

import type { ProviderProps } from '@/lib/types';
import 'client-only';
import { createContext } from 'react';
import type { ContextValue } from 'react-aria-components/slots';
import type { ChipContextValue } from './types';

/**
 * React context for sharing Chip configuration across components.
 */
export const ChipContext =
  createContext<ContextValue<ChipContextValue, HTMLDivElement>>(null);

/**
 * Context provider for setting default props across multiple Chip components.
 *
 * @param props - The provider props.
 * @param props.children - Child components that will receive the chip context.
 * @returns The chip context provider wrapping children.
 *
 * @example
 * ```tsx
 * <ChipProvider size="small" color="info">
 *   <Chip>Chip 1</Chip>
 *   <Chip>Chip 2</Chip>
 * </ChipProvider>
 * ```
 */
export function ChipProvider({
  children,
  ...props
}: ProviderProps<ChipContextValue>) {
  return <ChipContext.Provider value={props}>{children}</ChipContext.Provider>;
}
